import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import {
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { EcoFabSpeedDialModule } from '@ecodev/fab-speed-dial';
import { ElAutocompleteModule } from '@el-autocomplete';
import { ElDataListTableModule } from '@el-data-list-table';
import { MONACO_PATH, MonacoEditorModule } from '@materia-ui/ngx-monaco-editor';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { TranslateModule } from '@ngx-translate/core';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';

import { MaterialModule } from '../../material.module';
// Internal declarations
import { CommaSeparatedWithTwoDecimalDirective } from '../core/directives/comma-separated-with-two-decimals.directive';

import { ActionButtonComponent } from './components/action-button/action-button.component';
import { ApiDocEndpointComponent } from './components/api-doc-endpoint/api-doc-endpoint.component';
import { ButtonComponent } from './components/button/button.component';
import { DisplayDataInDLTComponent } from './components/display-data-in-dlt/display-data-in-dlt.component';
import { DragAndDropComponent } from './components/drag-and-drop/drag-and-drop.component';
import { ImagePickerComponent } from './components/image-picker/image-picker.component';
import { ImagePreviewComponent } from './components/image-preview/image-preview.component';
import { LogoLoaderComponent } from './components/logo-loader/logo-loader.component';
import { MonacoEditorComponent } from './components/monaco-editor/monaco-editor.component';
import { NoResultsFoundComponent } from './components/no-results-found/no-results-found.component';
import { NoteComponent } from './components/note/note.component';
import { PhoneNumberInputComponent } from './components/phone-number-input/phone-number-input.component';
import { DisplayPremiumSectionComponent } from './components/premium-displayer/premium-displayer.component';
import { PrettyjsonComponent } from './components/prettyjson/prettyjson.component';
import { ProcessCompletedComponent } from './components/process-completed/process-completed.component';
import { ProcessingDataComponent } from './components/processing-data/processing-data.component';
import { ReferenceFormFieldComponent } from './components/reference-form-field/reference-form-field.component';
import { SkeletonDataListTableComponent } from './components/skeleton-data-list-table/skeleton-data-list-table.component';
import { SkeletonDiaryComponent } from './components/skeleton-diary/skeleton-diary.component';
import { ZoomComponent } from './components/zoom/zoom.component';
import { CamelCasePipe } from './pipes/camel-case.pipe';
import { CurrencySpacePipe } from './pipes/currency.pipe';
import { FileSizePipe } from './pipes/file-size.pipe';
import { OEmbedToIframePipe } from './pipes/media-embed.pipe';
import { ConfirmationPopupComponent } from './popups/confirmation-popup/confirmation-popup.component';
import { DocumentUploaderComponent } from './popups/document-uploader/document-uploader.component';
import { ExitRecruitmentPopupComponent } from './popups/exit-recruitment/exit-recruitment.component';
import { PublicClientCredentialGeneratePopupComponent } from './popups/public-client-credential-generate-popup/public-client-credential-generate-popup.component';
import { PublicKeyGeneratePopupComponent } from './popups/public-key-generate-popup/public-key-generate-popup.component';
import { RichTextViewerComponent } from './popups/rich-text-viewer/rich-text-viewer.component';
import { ViewPermissionsComponent } from './popups/view-permissions/view-permissions.component';

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forChild(),
    MaterialModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,

    // Third-party
    MonacoEditorModule,
    ElDataListTableModule,
    EcoFabSpeedDialModule,
    NgxIntlTelInputModule,
    KeyboardShortcutsModule.forRoot(),
    ElAutocompleteModule,
    CKEditorModule,
    DragDropModule,
    MtxDatetimepickerModule,
  ],
  declarations: [
    CommaSeparatedWithTwoDecimalDirective,
    ActionButtonComponent,
    ApiDocEndpointComponent,
    ButtonComponent,
    DisplayDataInDLTComponent,
    DragAndDropComponent,
    ImagePickerComponent,
    ImagePreviewComponent,
    LogoLoaderComponent,
    MonacoEditorComponent,
    NoResultsFoundComponent,
    NoteComponent,
    PhoneNumberInputComponent,
    DisplayPremiumSectionComponent,
    PrettyjsonComponent,
    ProcessCompletedComponent,
    ProcessingDataComponent,
    ReferenceFormFieldComponent,
    SkeletonDataListTableComponent,
    SkeletonDiaryComponent,
    ZoomComponent,
    CamelCasePipe,
    CurrencySpacePipe,
    FileSizePipe,
    OEmbedToIframePipe,
    ConfirmationPopupComponent,
    DocumentUploaderComponent,
    PublicClientCredentialGeneratePopupComponent,
    PublicKeyGeneratePopupComponent,
    RichTextViewerComponent,
    ViewPermissionsComponent,
    ExitRecruitmentPopupComponent,
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MaterialModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,

    // Shared Components & Directives
    CommaSeparatedWithTwoDecimalDirective,
    ActionButtonComponent,
    ApiDocEndpointComponent,
    ButtonComponent,
    DisplayDataInDLTComponent,
    DragAndDropComponent,
    ImagePickerComponent,
    ImagePreviewComponent,
    LogoLoaderComponent,
    MonacoEditorComponent,
    NoResultsFoundComponent,
    NoteComponent,
    PhoneNumberInputComponent,
    DisplayPremiumSectionComponent,
    PrettyjsonComponent,
    ProcessCompletedComponent,
    ProcessingDataComponent,
    ReferenceFormFieldComponent,
    SkeletonDataListTableComponent,
    SkeletonDiaryComponent,
    ZoomComponent,
    CamelCasePipe,
    CurrencySpacePipe,
    FileSizePipe,
    OEmbedToIframePipe,
    ConfirmationPopupComponent,
    DocumentUploaderComponent,
    PublicClientCredentialGeneratePopupComponent,
    PublicKeyGeneratePopupComponent,
    RichTextViewerComponent,
    ViewPermissionsComponent,
    ExitRecruitmentPopupComponent,

    // Third-party modules
    ElDataListTableModule,
    ElAutocompleteModule,
    MonacoEditorModule,
  ],
  providers: [
    {
      provide: MONACO_PATH,
      useValue: 'https://unpkg.com/monaco-editor@0.21.3/min/vs',
    },
  ],
})
export class SharedModule {}

// ====================== RE-EXPORTS ======================

// Core Shared Components
export * from './components/action-button/action-button.component';
export * from './components/api-doc-endpoint/api-doc-endpoint.component';
export * from './components/button/button.component';
export * from './components/display-data-in-dlt/display-data-in-dlt.component';
export * from './components/drag-and-drop/drag-and-drop.component';
export * from './components/image-picker/image-picker.component';
export * from './components/image-preview/image-preview.component';
export * from './components/logo-loader/logo-loader.component';
export * from './components/monaco-editor/monaco-editor.component';
export * from './components/no-results-found/no-results-found.component';
export * from './components/note/note.component';
export * from './components/phone-number-input/phone-number-input.component';
export * from './components/premium-displayer/premium-displayer.component';
export * from './components/prettyjson/prettyjson.component';
export * from './components/process-completed/process-completed.component';
export * from './components/processing-data/processing-data.component';
export * from './components/reference-form-field/reference-form-field.component';
export * from './components/size-detector/size-detector.component';
export * from './components/skeleton-data-list-table/skeleton-data-list-table.component';
export * from './components/skeleton-diary/skeleton-diary.component';
export * from './components/zoom/zoom.component';

// Footer (Correct Path)
export * from '../core/components/footer/footer.component';

// Pipes
export * from './pipes/camel-case.pipe';
export * from './pipes/currency.pipe';
export * from './pipes/file-size.pipe';
export * from './pipes/media-embed.pipe';

// Popups
export * from './popups/confirmation-popup/confirmation-popup.component';
export * from './popups/document-uploader/document-uploader.component';
export * from './popups/public-client-credential-generate-popup/public-client-credential-generate-popup.component';
export * from './popups/public-key-generate-popup/public-key-generate-popup.component';
export * from './popups/rich-text-viewer/rich-text-viewer.component';
export * from './popups/exit-recruitment/exit-recruitment.component';
